import { BED_JOINT_GAP_MM, formatMm } from '@bw/core';
import type { ToolId } from './state/tools.js';

/**
 * SPECIFICATION.md clause 16 — the copy deck.
 *
 * Wording is part of the product: it names masonry things by their masonry
 * names, states what happens next, and never apologises. Keeping it in one
 * place stops it drifting as the UI grows.
 */
export const plural = (count: number, noun = 'brick'): string =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

export const clearanceNote = (perpendGapMm: number): string =>
  `${formatMm(perpendGapMm)} mm perpend and ${formatMm(BED_JOINT_GAP_MM)} mm bed clearance`;

export const COPY = {
  launch: 'Choose a brick and click on the drawing surface to place it.',

  laid: (name: string, x: number, y: number, relation: string | null): string =>
    `Laid ${name} at X ${formatMm(x)}, Y ${formatMm(y)} mm${relation ? ` · snapped ${relation}` : ''}.`,
  cannotLay: (gap: number): string => `Cannot lay here: requires ${clearanceNote(gap)}.`,

  copied: (count: number): string =>
    `Copied ${plural(count)}. Press Ctrl+V, position the pattern, then click.`,
  pasted: (count: number, relation: string | null): string =>
    `Pasted ${plural(count)}${relation ? ` · snapped ${relation}` : ''}. Click again to repeat the pattern.`,
  cannotPaste: (gap: number): string => `Cannot paste here: every brick needs ${clearanceNote(gap)}.`,
  pasteCancelled: 'Paste cancelled. The copied pattern is still available.',
  nothingToCopy: 'Select one or more bricks before copying.',
  nothingToPaste: 'Copy a brick selection before pasting.',

  painted: (name: string): string => `Recoloured ${name}. Click another brick to continue painting.`,
  paintMiss: 'No brick under the pointer. Paint mode remains active.',

  nothingToDelete: 'Select one or more bricks before deleting.',
  deleted: (count: number): string => `Deleted ${plural(count)}.`,
  cleared: 'Wall cleared. Use Undo to restore it.',
  selected: (count: number): string => `Selected ${plural(count)}. Ctrl+C copies the pattern.`,
  selectionCleared: 'Selection cleared. Drag around bricks to select a pattern.',
  selectedAll: (count: number): string => `Selected all ${plural(count)}.`,

  perpendApplied: (gap: number, count: number): string =>
    `Perpend joint set to ${formatMm(gap)} mm; re-coordinated ${plural(count)}.`,
  perpendUnchanged: (gap: number): string => `Perpend joint is already ${formatMm(gap)} mm.`,
  perpendUnchangedWall: 'The wall was left unchanged.',

  undone: 'Undid the last wall change.',
  redone: 'Redid the wall change.',

  rotated: (degrees: number, width: number, height: number): string =>
    `Preview rotated to ${degrees}° (${formatMm(width)} × ${formatMm(height)} mm).`,
  snap: (on: boolean): string => `Smart snap is ${on ? 'on' : 'off'}.`,

  // ---- walls ----
  wallAdded: (name: string): string =>
    `Added ${name}, carrying on from the end of the last wall. Lay its bricks, then arrange the plan.`,
  wallDeleted: (name: string): string => `Deleted ${name}. Ctrl+Z puts it back.`,
  wallImported: (name: string, count: number, dropped: number): string =>
    dropped === 0
      ? `Imported ${name} — ${plural(count)}. Arrange it in the plan.`
      : `Imported ${name} — ${plural(count)}; omitted ${plural(dropped)} that could not be placed ` +
        `with valid clearance. Arrange it in the plan.`,

  measureNoBricks: 'No bricks available to measure',
  measureFirst: 'Click the first brick corner',
  measureFirstFixed: (x: number, y: number): string =>
    `First point: X ${formatMm(x)}, Y ${formatMm(y)} mm. Click the second corner.`,
  measureNothing: 'No measurement · Press M to begin',

  // ---- plan view (phase 2, M5) ----
  savedImage: (name: string): string => `Saved ${name}`,
  pickedBrick: (wall: string, id: number): string =>
    `Brick ${id} on ${wall} · selected from the 3D view`,

  planIdle: 'Plan · Drag a wall to position it · Grab the round handle to turn it',
  planSelected: (name: string): string =>
    `Plan · ${name} selected · Drag to move · Handle turns it · Arrows nudge · R turns 90°`,
  planRotating: (name: string): string =>
    `Plan · Turning ${name} · Snapping to 15° · Hold Alt for any angle`,
  planPlaced: (
    name: string,
    placement: { origin: { x: number; z: number }; rotationDeg: number },
  ): string =>
    `${name} placed at X ${formatMm(placement.origin.x)}, Z ${formatMm(placement.origin.z)} mm, turned ${formatMm(placement.rotationDeg)}°`,
  // Every resting status leads with its tool, so the mode badge and the
  // sentence beside it always agree.
  // The wall leads, because one wall is always selected in the plan now and it
  // is the one every arrow key and placement field is about.
  planOpen: (name: string, ends: number): string =>
    `Plan · ${name} selected · Outline not closed · ${ends} wall ${ends === 1 ? 'end joins' : 'ends join'} nothing`,
  planClosed: (name: string, corners: number, areaMm2: number): string =>
    `Plan · ${name} selected · Outline closed · ${corners} corners · ${(areaMm2 / 1_000_000).toFixed(2)} m²`,
  measureNoCorners: 'There are no brick corners to measure. Lay at least one brick first.',

  moved: (count: number, relation: string | null): string =>
    `Moved ${plural(count)}${relation ? ` · snapped ${relation}` : ''}.`,
  cannotMove: (gap: number): string =>
    `Cannot move there: every brick needs ${clearanceNote(gap)}.`,
  moveCancelled: 'Move cancelled. The bricks are back where they were.',
  nudgeRefused: (gap: number): string => `Nudge blocked: that would leave less than ${clearanceNote(gap)}.`,

  generated: (placed: number, skipped: number): string =>
    skipped === 0
      ? `Generated ${plural(placed)}.`
      : `Generated ${plural(placed)}; skipped ${plural(skipped)} that meet existing work.`,
  sketchRectangle: 'Drag a rectangle to draw a closed shape, or switch to a free line.',

  sketchStart: 'Sketch: click to place points. Click the first point again to close the shape.',
  sketchPoint: (count: number, length: number): string =>
    `${count} point${count === 1 ? '' : 's'} · last run ${formatMm(length)} mm. Click the first point to close.`,
  sketchClosed: (role: string, points: number): string =>
    `Closed a ${role} shape with ${points} points.`,
  sketchOpen: (count: number): string => `Kept ${count} open line${count === 1 ? '' : 's'}.`,
  sketchTooFew: 'A closed shape needs at least three points.',
  sketchFilled: (placed: number, skipped: number): string =>
    skipped === 0
      ? `Filled the shape with ${plural(placed)}.`
      : `Filled the shape with ${plural(placed)}; skipped ${plural(skipped)} that meet existing work.`,
  /**
   * A course set out from a corner that could not reach it.
   *
   * Only possible when the wall's own dimensions do not suit the bond — half a
   * wall thickness is always five millimetres off a whole unit, so a wall with
   * a bonded corner at one end and a plain join at the other can never finish
   * flush. Worth saying, because the gap it leaves runs the height of the wall
   * and nothing else in the drawing points at it.
   */
  cornerShortfall: (mm: number): string =>
    `The corner is ${formatMm(mm)} mm short: this wall's length does not suit the bond. ` +
    `Try a length a whole number of units longer or shorter.`,
  sketchCleared: 'Sketch cleared.',
  sketchMovedPoint: (x: number, y: number): string =>
    `Moved the point to X ${formatMm(x)}, Y ${formatMm(y)} mm.`,
  sketchMoved: (what: 'shape' | 'segment', dx: number, dy: number): string =>
    `Moved the ${what === 'shape' ? 'shape' : 'line'} by X ${formatMm(dx)}, Y ${formatMm(dy)} mm.`,
  sketchSelected: (what: 'shape' | 'segment', points: number): string =>
    what === 'shape'
      ? `Selected a shape of ${points} points. Drag to move it; drag a corner to move that corner.`
      : 'Selected a line. Drag to move it; drag an end to move that end.',
  sketchDeselected: 'Nothing selected in the drawing.',
  sketchAddedPoint: (x: number, y: number): string =>
    `Added a point at X ${formatMm(x)}, Y ${formatMm(y)} mm.`,
  sketchNoLine: 'Double-click on a drawn line to add a point to it.',
  sketchDeletedShape: 'Deleted the shape.',
  sketchDeletedLine: 'Deleted the line.',
  generateEmpty: 'Draw an envelope before generating a bond.',

  /**
   * The example is the one thing here that needs the network: it is a
   * two-and-a-half megabyte project, and making every visitor carry it so a
   * few can open it offline is the wrong way round. Once opened it is kept, so
   * this is only ever the first attempt.
   */
  exampleOffline:
    'The example house is downloaded when you open it, and there is no connection. ' +
    'Everything else here works offline — try again once you are back on.',
  exampleRefused: (status: number): string =>
    `The server could not send the example house (${status}).`,

  opened: (name: string): string => `Opened ${name}.`,
  saved: (name: string): string => `Saved ${name}.`,
  newProject: 'New wall. Choose a brick and click to start laying.',
  imported: (count: number, dropped: number): string =>
    dropped === 0
      ? `Imported ${plural(count)}.`
      : `Imported ${plural(count)}; omitted ${plural(dropped)} that could not be placed with valid clearance.`,
} as const;

/** Clause 16.1 — the resting status line for each tool. */
export function restingStatus(
  tool: ToolId,
  context: {
    readonly brickName: string;
    readonly brickWidth: number;
    readonly brickHeight: number;
    readonly clipboardCount: number;
    readonly hasBricks: boolean;
    readonly measureStage: 'none' | 'first' | 'complete';
    readonly measureSummary: string;
    readonly planStatus: string;
  },
): string {
  switch (tool) {
    case 'build':
      return `Build · ${context.brickName} ${formatMm(context.brickWidth)} × ${formatMm(context.brickHeight)} mm · Click to lay · R rotates`;
    case 'paint':
      return 'Paint · Click laid bricks to recolour · Choose a brick tool to resume building';
    case 'paste':
      return `Paste · ${plural(context.clipboardCount)} · Click repeatedly · Esc finishes`;
    case 'sketch':
      return 'Sketch · Draw the outline · Close a shape to fill or lay it · Esc ends the line';
    case 'plan':
      return context.planStatus;
    case 'pan':
      return 'Pan · Drag with the left mouse button · Mouse wheel zooms · Esc finishes';
    case 'measure':
      if (context.measureStage === 'complete') return `Measure · ${context.measureSummary} · Click for a new measurement`;
      if (context.measureStage === 'first') return 'Measure · First corner fixed · Click the second brick corner · Esc finishes';
      return context.hasBricks
        ? 'Measure · Click the first brick corner · M selects this tool · Esc finishes'
        : 'Measure · Lay at least one brick before choosing points · Esc finishes';
    default:
      return 'Select · Drag to marquee or move · Arrows nudge · Shift adds · Ctrl toggles';
  }
}
